import { LatLon } from '../domain/types.ts';
import { SearchService } from './searchService.ts';

export interface UserLocationResult {
  coords: LatLon;
  label: string;
  streetName: string;
  district?: string;
  city?: string;
  region: 'north' | 'central' | 'south';
  source: 'gps' | 'ip' | 'fallback';
  accuracy?: number;
}

interface NominatimReverseResponse {
  display_name: string;
  address?: {
    road?: string;
    pedestrian?: string;
    street?: string;
    footway?: string;
    path?: string;
    neighbourhood?: string;
    suburb?: string;
    quarter?: string;
    city_district?: string;
    district?: string;
    county?: string;
    town?: string;
    city?: string;
    state?: string;
    country?: string;
  };
}

export class LocationService {
  /**
   * Lấy vị trí người chơi với cơ chế 2 pha:
   * Phase 1: navigator.geolocation (GPS thiết bị chính xác cao)
   * Phase 2: Fallback định vị qua IP nếu từ chối quyền hoặc lỗi/timeout
   * Sau đó Reverse Geocode qua Nominatim để lấy tên ngõ/đường thực tế.
   */
  public static async getCurrentLocation(): Promise<UserLocationResult> {
    let lat: number | null = null;
    let lon: number | null = null;
    let accuracy: number | undefined = undefined;
    let source: 'gps' | 'ip' | 'fallback' = 'gps';
    let cityHint = '';

    // Phase 1: Thử lấy GPS qua navigator.geolocation
    const gpsRes = await LocationService.getGpsPosition(6000);
    if (gpsRes) {
      lat = gpsRes.lat;
      lon = gpsRes.lon;
      accuracy = gpsRes.accuracy;
      source = 'gps';
    } else {
      // Phase 2: Fallback qua IP Address
      console.warn('[LocationService] GPS không khả dụng hoặc bị từ chối, chuyển sang định vị IP...');
      const ipRes = await LocationService.getIpPosition(5000);
      if (ipRes) {
        lat = ipRes.lat;
        lon = ipRes.lon;
        cityHint = ipRes.city || '';
        source = 'ip';
      } else {
        // Fallback cuối cùng: Mặc định ngõ phố Hà Nội
        console.warn('[LocationService] Không thể định vị IP, dùng vị trí mặc định!');
        const def = SearchService.FALLBACK_LOCATIONS[0]!;
        lat = def.coords.lat;
        lon = def.coords.lon;
        source = 'fallback';
      }
    }

    // Reverse Geocode qua Nominatim để lấy tên đường phố
    const geocode = await LocationService.reverseGeocode(lat, lon, cityHint);

    return {
      coords: { lat, lon },
      label: geocode.label,
      streetName: geocode.streetName,
      district: geocode.district,
      city: geocode.city,
      region: geocode.region,
      source,
      accuracy,
    };
  }

  /**
   * Phase 1: Lấy tọa độ GPS từ trình duyệt
   */
  public static getGpsPosition(
    timeoutMs = 6000
  ): Promise<{ lat: number; lon: number; accuracy?: number } | null> {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      let resolved = false;
      const timer = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          resolve(null);
        }
      }, timeoutMs);

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timer);
            resolve({
              lat: pos.coords.latitude,
              lon: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
            });
          }
        },
        (err) => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timer);
            console.warn('[LocationService] Lỗi GPS Geolocation:', err.message);
            resolve(null);
          }
        },
        {
          enableHighAccuracy: true,
          timeout: timeoutMs,
          maximumAge: 30000,
        }
      );
    });
  }

  /**
   * Phase 2: Định vị vị trí qua IP (API miễn phí)
   */
  public static async getIpPosition(
    timeoutMs = 5000
  ): Promise<{ lat: number; lon: number; city?: string } | null> {
    // Thử dịch vụ 1: ipapi.co
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      const res = await fetch('https://ipapi.co/json/', {
        signal: controller.signal,
        headers: { 'Accept': 'application/json' },
      });
      clearTimeout(timer);

      if (res.ok) {
        const data = await res.json();
        if (data && typeof data.latitude === 'number' && typeof data.longitude === 'number') {
          return {
            lat: data.latitude,
            lon: data.longitude,
            city: data.city || data.region,
          };
        }
      }
    } catch {
      // Bỏ qua thử tiếp dịch vụ 2
    }

    // Thử dịch vụ 2: ipwho.is
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      const res = await fetch('https://ipwho.is/', {
        signal: controller.signal,
        headers: { 'Accept': 'application/json' },
      });
      clearTimeout(timer);

      if (res.ok) {
        const data = await res.json();
        if (data && data.success && typeof data.latitude === 'number') {
          return {
            lat: data.latitude,
            lon: data.longitude,
            city: data.city || data.region,
          };
        }
      }
    } catch {
      // Cả 2 đều lỗi
    }

    return null;
  }

  /**
   * Reverse Geocode từ tọa độ (lat, lon) qua Nominatim OpenStreetMap
   */
  public static async reverseGeocode(
    lat: number,
    lon: number,
    fallbackCity = ''
  ): Promise<{
    label: string;
    streetName: string;
    district: string;
    city: string;
    region: 'north' | 'central' | 'south';
  }> {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);

      const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`;
      const res = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Accept-Language': 'vi,en;q=0.9',
        },
      });
      clearTimeout(timer);

      if (res.ok) {
        const data = (await res.json()) as NominatimReverseResponse;
        if (data && data.address) {
          const addr = data.address;
          const streetName =
            addr.road ||
            addr.pedestrian ||
            addr.street ||
            addr.footway ||
            addr.path ||
            addr.neighbourhood ||
            addr.suburb ||
            'Tuyến phố hiện tại';

          const district =
            addr.city_district || addr.district || addr.suburb || addr.quarter || addr.county || '';
          const city = addr.city || addr.town || addr.state || fallbackCity || 'Việt Nam';

          const labelParts = [streetName];
          if (district && district !== streetName) labelParts.push(district);
          if (city && city !== district) labelParts.push(city);
          const label = labelParts.join(', ');

          const region = SearchService.detectRegion(label);

          return {
            label,
            streetName,
            district,
            city,
            region,
          };
        }
      }
    } catch (err) {
      console.warn('[LocationService] Lỗi Nominatim Reverse Geocoding:', err);
    }

    // Fallback nếu Nominatim lỗi
    const defaultLabel = fallbackCity
      ? `Khu phố tại ${fallbackCity}`
      : 'Vị trí hiện tại của bạn';
    return {
      label: defaultLabel,
      streetName: 'Khu phố hiện tại',
      district: '',
      city: fallbackCity || 'Việt Nam',
      region: SearchService.detectRegion(defaultLabel),
    };
  }
}
